/*
  "Nedavno gledano" — mini kartice iz localStorage (da_recently_viewed).

  Kesirani zapis drzi sliku, cenu i URL onakve kakvi su bili u trenutku
  posete. Kad vlasnik zameni sliku proizvoda u adminu, stari CDN fajl
  nestane i kupcu zauvek ostane ikona slomljene slike; isto tako, cena
  zapamcena na srpskoj prodavnici ostaje u RSD i kad kupac gleda /sl-si.

  Zato se zapis popravlja sam: kad slika pukne ili kad se valuta ne
  poklapa, jednom se povuce /products/<handle>.js (kroz Shopify.routes.root,
  pa putanja prati jezik) i osvezeni podaci se upisu nazad u localStorage.
*/
const RECENTLY_VIEWED_KEY = 'da_recently_viewed';

/*
  Cena se ne formatira preko Intl, jer Intl za sl-SI daje "7,95 €", a tema
  prikazuje "€7,95". Umesto toga se iz uzorka koji je Liquid renderovao
  ({{ 123456 | money }} = 1234,56) izvlace simbol, njegova pozicija i
  separatori, pa osvezena cena izgleda identicno kao svaka druga na stranici.
*/
const buildMoneyFormatter = (sample) => {
  const text = String(sample || '');
  const match = text.match(/\d[\d., \s]*\d|\d/);
  if (!match) return null;
  const number = match[0];
  const prefix = text.slice(0, match.index);
  const suffix = text.slice(match.index + number.length);
  const separators = number.replace(/\d/g, '');
  const decimals = number.replace(/\D/g, '').length >= 6 ? 2 : 0;
  const decimalSeparator = decimals ? separators.slice(-1) : '';
  const thousandsSeparator = separators.length > (decimals ? 1 : 0) ? separators.charAt(0) : '';
  return (cents) => {
    const amount = decimals ? (cents / 100).toFixed(2) : String(Math.round(cents / 100));
    const parts = amount.split('.');
    const whole = thousandsSeparator
      ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator)
      : parts[0];
    return prefix + whole + (decimals ? decimalSeparator + parts[1] : '') + suffix;
  };
};

// Znak valute ("€" ili "RSD") iz istog uzorka — sluzi da se prepozna cena
// zapamcena u drugoj valuti, ukljucujuci stare zapise bez polja currency.
const moneyToken = (sample) => String(sample || '').replace(/[\d., \s]/g, '');

const withImageWidth = (url) => {
  if (!url) return '';
  return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=400';
};

const fetchProduct = async (handle) => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const response = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js');
  if (!response.ok) return null;
  return response.json();
};

if (!customElements.get('recently-viewed')) {
  customElements.define(
    'recently-viewed',
    class RecentlyViewed extends HTMLElement {
      connectedCallback() {
        const STORAGE_CAP = 12;
        let current = null;
        try {
          current = JSON.parse(this.querySelector('[data-current-product]').textContent);
        } catch (error) {
          return;
        }
        let stored = [];
        try {
          stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY)) || [];
        } catch (error) {
          stored = [];
        }
        if (!Array.isArray(stored)) stored = [];

        const previous = stored.filter((item) => item && item.id !== current.id);
        try {
          localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify([current, ...previous].slice(0, STORAGE_CAP)));
        } catch (error) {
          // localStorage nedostupan (privatni rezim) — samo preskoci upis
        }

        this.formatMoney = buildMoneyFormatter(this.dataset.moneySample);
        this.currencyToken = moneyToken(this.dataset.moneySample);
        this.repaired = 0;

        const max = parseInt(this.dataset.max || '8', 10);
        const toShow = previous.slice(0, max);
        if (!toShow.length) return;

        const row = this.querySelector('[data-recently-viewed-row]');
        toShow.forEach((item) => {
          const card = document.createElement('a');
          card.className = 'recently-viewed__card';
          card.href = item.url;
          const img = document.createElement('img');
          img.className = 'recently-viewed__img';
          img.src = item.img || '';
          img.alt = item.title || '';
          img.loading = 'lazy';
          img.width = 140;
          img.height = 140;
          const title = document.createElement('span');
          title.className = 'recently-viewed__name';
          title.textContent = item.title || '';
          const price = document.createElement('span');
          price.className = 'recently-viewed__price';
          price.textContent = item.price || '';
          card.append(img, title, price);
          row.append(card);

          const nodes = { card, img, price, title };
          img.addEventListener('error', () => this.refresh(item, nodes));
          if (!item.img) this.refresh(item, nodes);
          if (this.staleCurrency(item)) this.refresh(item, nodes);
        });
        this.removeAttribute('hidden');
      }

      // Cena je iz druge valute ako u njoj nema znaka valute tekuceg trzista.
      staleCurrency(item) {
        if (!this.formatMoney || !this.currencyToken || !item.price) return false;
        if (item.currency && this.dataset.currency) return item.currency !== this.dataset.currency;
        return item.price.indexOf(this.currencyToken) === -1;
      }

      async refresh(item, nodes) {
        if (item.refreshing || this.repaired >= 8) return;
        item.refreshing = true;
        this.repaired += 1;
        let product = null;
        try {
          product = await fetchProduct(item.handle);
        } catch (error) {
          product = null;
        }
        if (!product) {
          // proizvod vise ne postoji — zapis se ne brise, samo se slika
          // zameni praznim mestom da kartica ne prikazuje slomljenu ikonu
          this.showMissing(nodes);
          return;
        }
        const fresh = {
          ...item,
          title: product.title || item.title,
          url: product.url || item.url,
          img: withImageWidth(product.featured_image),
          price_cents: product.price,
          currency: this.dataset.currency || item.currency,
        };
        delete fresh.refreshing;
        if (this.formatMoney && typeof product.price === 'number') {
          fresh.price = this.formatMoney(product.price);
        }
        if (fresh.img) {
          nodes.img.src = fresh.img;
        } else {
          this.showMissing(nodes);
        }
        nodes.card.href = fresh.url;
        nodes.price.textContent = fresh.price || '';
        nodes.title.textContent = fresh.title || '';
        this.persist(fresh);
      }

      showMissing(nodes) {
        const placeholder = document.createElement('span');
        placeholder.className = 'recently-viewed__img recently-viewed__img--missing';
        placeholder.setAttribute('aria-hidden', 'true');
        nodes.img.replaceWith(placeholder);
      }

      persist(fresh) {
        try {
          const list = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY)) || [];
          if (!Array.isArray(list)) return;
          const next = list.map((entry) => (entry && entry.handle === fresh.handle ? fresh : entry));
          localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next));
        } catch (error) {
          // localStorage nedostupan — popravka ostaje samo za ovu posetu
        }
      }
    }
  );
}
